import React, { useEffect, useState, useRef } from 'react';
import { getTenant } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { SearchIcon, CreditCardIcon, AlertCircleIcon, UserIcon, MailIcon, PhoneIcon, DollarSignIcon, CheckIcon, XIcon, ChevronRightIcon, ShieldCheckIcon } from 'lucide-react';
import { MetadataItem } from '../../../shared/ui/MetadataEditor';
import { CommonPaymentOptions } from './CommonPaymentOptions';
interface CardOnFilePaymentProps {
  onExpiryDateChange?: (date: string) => void;
  onAmountChange?: (amount: string) => void;
  onCurrencyChange?: (currency: string) => void;
  onReferenceChange?: (reference: string) => void;
  onSuccess?: () => void;
}
interface Customer {
  id: string;
  name: string;
  email: string;
  phone: string;
  metadata: {
    ltv: string;
    since: string;
    status: string;
  };
  cards: Card[];
}
interface Card {
  id: string;
  brand: string;
  last4: string;
  expiry: string;
  isExpired: boolean;
}
const mockCustomers: Customer[] = [{
  id: 'cus_1',
  name: 'John Smith',
  email: 'john@example.com',
  phone: '+1 (555) 123-4567',
  metadata: {
    ltv: `${getTenant().currency.code} 45,000`,
    since: 'Jan 2023',
    status: 'Premium'
  },
  cards: [{
    id: 'card_1',
    brand: 'Visa',
    last4: '4242',
    expiry: '12/27',
    isExpired: false
  }, {
    id: 'card_2',
    brand: 'Mastercard',
    last4: '5555',
    expiry: '01/22',
    isExpired: true
  }, {
    id: 'card_3',
    brand: 'Amex',
    last4: '9012',
    expiry: '06/28',
    isExpired: false
  }, {
    id: 'card_4',
    brand: 'Visa',
    last4: '1881',
    expiry: '03/24',
    isExpired: true
  }]
}, {
  id: 'cus_2',
  name: 'Sarah Johnson',
  email: 'sarah@example.com',
  phone: '+1 (555) 987-6543',
  metadata: {
    ltv: `${getTenant().currency.code} 12,500`,
    since: 'Mar 2024',
    status: 'Standard'
  },
  cards: [{
    id: 'card_5',
    brand: 'Mastercard',
    last4: '3000',
    expiry: '09/26',
    isExpired: false
  }, {
    id: 'card_6',
    brand: 'Visa',
    last4: '7744',
    expiry: '11/23',
    isExpired: true
  }, {
    id: 'card_7',
    brand: 'Amex',
    last4: '2200',
    expiry: '04/28',
    isExpired: false
  }]
}, {
  id: 'cus_3',
  name: 'Michael Brown',
  email: 'michael@example.com',
  phone: '+1 (555) 456-7890',
  metadata: {
    ltv: `${getTenant().currency.code} 8,200`,
    since: 'Oct 2024',
    status: 'New'
  },
  cards: [{
    id: 'card_8',
    brand: 'Visa',
    last4: '6633',
    expiry: '02/27',
    isExpired: false
  }, {
    id: 'card_9',
    brand: 'Mastercard',
    last4: '4400',
    expiry: '08/23',
    isExpired: true
  }, {
    id: 'card_10',
    brand: 'Visa',
    last4: '1199',
    expiry: '05/24',
    isExpired: true
  }]
}];
export function CardOnFilePayment({
  onExpiryDateChange,
  onAmountChange,
  onCurrencyChange,
  onReferenceChange,
  onSuccess
}: CardOnFilePaymentProps) {
  const {
    t
  } = useTranslation('payments');
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState(getTenant().currency.code);
  const [reference, setReference] = useState('');
  const [description, setDescription] = useState('');
  const [metadata, setMetadata] = useState<MetadataItem[]>([]);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleAmountChange = (v: string) => {
    setAmount(v);
    onAmountChange?.(v);
  };
  const handleCurrencyChange = (v: string) => {
    setCurrency(v);
    onCurrencyChange?.(v);
  };
  const handleReferenceChange = (v: string) => {
    setReference(v);
    onReferenceChange?.(v);
  };
  const handleExpiryDateChange = (v: string) => {
    setExpiryDate(v);
    onExpiryDateChange?.(v);
  };
  const filteredCustomers = searchTerm.trim() ? mockCustomers.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()) || c.email.toLowerCase().includes(searchTerm.toLowerCase()) || c.phone.includes(searchTerm)) : mockCustomers;
  const handleSelectCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    setSearchTerm('');
    setIsDropdownOpen(false);
    const validCards = customer.cards.filter(c => !c.isExpired);
    setSelectedCardId(validCards.length > 0 ? validCards[0].id : null);
  };
  const handleClearCustomer = () => {
    setSelectedCustomer(null);
    setSelectedCardId(null);
    setSearchTerm('');
  };
  const handleConfirmCharge = () => {
    setShowPreviewModal(false);
    onSuccess?.();
  };
  const inputClass = 'block w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors placeholder:text-gray-400';
  const validCards = selectedCustomer?.cards.filter(c => !c.isExpired) || [];
  const selectedCard = selectedCustomer?.cards.find(c => c.id === selectedCardId);
  return <div className="space-y-5" data-id="element-6954">
      {/* ─── CUSTOMER SELECTION ─── */}
      <div className="bg-white rounded-xl border border-gray-200" data-id="element-6955">
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-6956">
          <h2 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-6957">
            <UserIcon size={16} className="mr-2 text-[#9F140F]" data-id="element-6958" />
            {t('components.BulkPaymentPreview.customer')}
          </h2>
        </div>

        {!selectedCustomer ? <div className="p-6" ref={dropdownRef} data-id="element-6959">
            <div className="relative" data-id="element-6960">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-6961">
                <SearchIcon size={16} className="text-gray-400" data-id="element-6962" />
              </div>
              <input type="text" className={`${inputClass} pl-10`} placeholder={t('components.CardOnFilePayment.attrs.search_by_name_email_or_phone')} value={searchTerm} onChange={e => {
            setSearchTerm(e.target.value);
            setIsDropdownOpen(true);
          }} onFocus={() => setIsDropdownOpen(true)} data-id="element-6963" />

              {isDropdownOpen && <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto" data-id="element-6964">
                  {filteredCustomers.length > 0 ? <div data-id="element-6965">
                      {filteredCustomers.map((customer, idx) => <div key={customer.id} className={`px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors ${idx < filteredCustomers.length - 1 ? 'border-b border-gray-100' : ''}`} onMouseDown={e => {
                e.preventDefault();
                handleSelectCustomer(customer);
              }} data-id="element-6966">
                          <div className="flex justify-between items-center" data-id="element-6967">
                            <div data-id="element-6968">
                              <p className="text-sm font-medium text-gray-900" data-id="element-6969">
                                {customer.name}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5" data-id="element-6970">
                                {customer.email}
                              </p>
                            </div>
                            <span className="text-xs text-gray-400" data-id="element-6971">
                              {customer.cards.filter(c => !c.isExpired).length}{' '}
                              {t('components.CardOnFilePayment.cards')}
                            </span>
                          </div>
                        </div>)}
                    </div> : <div className="px-4 py-6 text-center text-sm text-gray-500" data-id="element-6972">
                      {t('components.CardOnFilePayment.no_customers_found')}
                    </div>}
                </div>}
            </div>
          </div> : <div className="px-6 py-4 flex items-center justify-between" data-id="element-6973">
            <div className="flex items-center min-w-0" data-id="element-6974">
              <div className="h-9 w-9 rounded-full bg-[#E2231A]/10 flex items-center justify-center flex-shrink-0 mr-3" data-id="element-6975">
                <UserIcon size={16} className="text-[#9F140F]" data-id="element-6976" />
              </div>
              <div className="min-w-0" data-id="element-6977">
                <div className="flex items-center gap-2" data-id="element-6978">
                  <p className="text-sm font-semibold text-gray-900 truncate" data-id="element-6979">
                    {selectedCustomer.name}
                  </p>
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#E2231A]/10 text-[#E2231A] flex-shrink-0" data-id="element-6980">
                    {selectedCustomer.metadata.status}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500" data-id="element-6981">
                  <span className="flex items-center" data-id="element-6982">
                    <MailIcon size={12} className="mr-1 text-gray-400" data-id="element-6983" />
                    {selectedCustomer.email}
                  </span>
                  <span className="flex items-center" data-id="element-6984">
                    <PhoneIcon size={12} className="mr-1 text-gray-400" data-id="element-6985" />
                    {selectedCustomer.phone}
                  </span>
                </div>
              </div>
            </div>
            <button onClick={handleClearCustomer} className="text-xs font-medium text-[#9F140F] hover:text-[#9F140F]/75 transition-colors flex-shrink-0 ml-4" data-id="element-6986">
              {t('components.CardOnFilePayment.change')}
            </button>
          </div>}
      </div>

      {/* ─── PAYMENT METHOD ─── */}
      {selectedCustomer && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-6987">
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-6988">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-6989">
              <CreditCardIcon size={16} className="mr-2 text-[#9F140F]" data-id="element-6990" />
              {t('components.CardOnFilePayment.payment_method')}
            </h3>
          </div>

          {selectedCustomer.cards.length > 0 ? <div data-id="element-6991">
              {selectedCustomer.cards.map((card, idx) => <div key={card.id} className={`flex items-center px-6 py-4 transition-colors ${card.isExpired ? 'opacity-50 cursor-not-allowed bg-gray-50/50' : selectedCardId === card.id ? 'bg-[#E2231A]/5 cursor-pointer' : 'hover:bg-gray-50 cursor-pointer'} ${idx < selectedCustomer.cards.length - 1 ? 'border-b border-gray-200' : ''}`} onClick={() => {
          if (!card.isExpired) setSelectedCardId(card.id);
        }} data-id="element-6992">
                  <input type="radio" name="savedCard" className="h-4 w-4 text-[#E2231A] focus:ring-[#E2231A]/20 border-gray-300 flex-shrink-0" checked={selectedCardId === card.id} disabled={card.isExpired} onChange={() => {
            if (!card.isExpired) setSelectedCardId(card.id);
          }} data-id="element-6993" />

                  <div className="ml-4 flex-1 flex items-center justify-between" data-id="element-6994">
                    <div className="flex items-center" data-id="element-6995">
                      <div className={`h-8 w-12 border rounded flex items-center justify-center mr-3 overflow-hidden ${card.isExpired ? 'bg-gray-100 border-gray-200' : 'bg-white border-gray-200'}`} data-id="element-6996">
                        {card.brand.toLowerCase() === 'visa' ? <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/akmkk18tu8vmgxet3nfyip/2db2784c-a6fd-4102-a1f3-1a3d11f02729/visa_logo.png" alt={t('components.CardOnFilePayment.attrs.visa')} className="h-4 object-contain" data-id="element-6997" /> : card.brand.toLowerCase() === 'mastercard' ? <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/akmkk18tu8vmgxet3nfyip/2db2784c-a6fd-4102-a1f3-1a3d11f02729/mastercard_logo.png" alt={t('components.CardOnFilePayment.attrs.mastercard')} className="h-6 object-contain" data-id="element-6998" /> : card.brand.toLowerCase() === 'amex' ? <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/akmkk18tu8vmgxet3nfyip/2db2784c-a6fd-4102-a1f3-1a3d11f02729/amex_logo.svg" alt={t('components.CardOnFilePayment.attrs.amex')} className="h-6 object-contain" data-id="element-6999" /> : <span className={`text-[10px] font-bold uppercase ${card.isExpired ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7000">
                            {card.brand}
                          </span>}
                      </div>
                      <div data-id="element-7001">
                        <p className={`text-sm font-medium ${card.isExpired ? 'text-gray-400' : 'text-gray-900'}`} data-id="element-7002">
                          {card.brand} •••• {card.last4}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5" data-id="element-7003">
                          <p className={`text-xs ${card.isExpired ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7004">
                            {t('components.CardOnFilePayment.expires')}{' '}
                            {card.expiry}
                          </p>
                          {card.isExpired && <span className="text-[10px] font-medium text-red-400 bg-red-50 px-1.5 py-0.5 rounded" data-id="element-7005">
                              {t('components.CardOnFilePayment.expired')}
                            </span>}
                        </div>
                      </div>
                    </div>
                    {!card.isExpired && selectedCardId === card.id && <CheckIcon size={16} className="text-[#9F140F] flex-shrink-0" data-id="element-7006" />}
                  </div>
                </div>)}
            </div> : <div className="px-6 py-10 text-center" data-id="element-7007">
              <AlertCircleIcon size={20} className="mx-auto text-gray-400 mb-2" data-id="element-7008" />

              <p className="text-sm font-medium text-gray-700" data-id="element-7009">
                {t('components.CardOnFilePayment.no_cards_on_file')}
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-7010">
                {t('components.CardOnFilePayment.this_customer_has_no_saved_cards')}
              </p>
            </div>}
        </div>}

      {/* ─── CHARGE DETAILS ─── */}
      {selectedCustomer && selectedCardId && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-7011">
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-7012">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-7013">
              <DollarSignIcon size={16} className="mr-2 text-[#9F140F]" data-id="element-7014" />
              {t('components.CardOnFilePayment.charge_details')}
            </h3>
          </div>
          <div className="p-6 space-y-4" data-id="element-7015">
            <div data-id="element-7016">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-7017">
                {t('components.CardOnFilePayment.payment_amount')}{' '}
                <span className="text-red-400" data-id="element-7018">*</span>
              </label>
              <div className="relative" data-id="element-7019">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7020">
                  <span className="text-sm font-medium text-gray-500" data-id="element-7021">
                    {t('components.BulkPaymentListSimplified.usd')}
                  </span>
                </div>
                <input type="text" className={`${inputClass} pl-12`} placeholder="0.00" value={amount} onChange={e => handleAmountChange(e.target.value)} data-id="element-7022" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-7023">
              <div data-id="element-7024">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-7025">
                  {t('components.BulkPaymentPreview.reference')}
                </label>
                <input type="text" className={inputClass} placeholder={t('components.CardOnFilePayment.attrs.eg_invoice_1234')} value={reference} onChange={e => handleReferenceChange(e.target.value)} data-id="element-7026" />
              </div>
              <div data-id="element-7027">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-7028">
                  {t('components.BulkPaymentPreview.description')}
                </label>
                <input type="text" className={inputClass} placeholder={t('components.CardOnFilePayment.attrs.eg_monthly_subscription')} value={description} onChange={e => setDescription(e.target.value)} data-id="element-7029" />
              </div>
            </div>
          </div>
        </div>}

      {/* ─── ADDITIONAL OPTIONS ─── */}
      {selectedCustomer && selectedCardId && <CommonPaymentOptions metadata={metadata} redirectUrl={redirectUrl} expiryDate={expiryDate} onMetadataChange={setMetadata} onRedirectUrlChange={setRedirectUrl} onExpiryDateChange={handleExpiryDateChange} hideExpiry={true} hideRedirect={true} data-id="element-7030" />}

      {/* ─── PREVIEW CHARGE BUTTON ─── */}
      {selectedCustomer && selectedCardId && <div className="flex justify-end" data-id="element-7031">
          <button onClick={() => {
        if (selectedCustomer && selectedCardId && amount) {
          setShowPreviewModal(true);
        }
      }} disabled={!amount} className="px-6 py-2.5 bg-[#E2231A] hover:bg-[#C41B14] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors shadow-sm flex items-center" data-id="element-7032">
            {t('components.CardOnFilePayment.preview_charge')}
            <ChevronRightIcon size={16} className="ml-1.5" data-id="element-7033" />
          </button>
        </div>}

      {/* ─── PREVIEW MODAL ─── */}
      {showPreviewModal && selectedCustomer && selectedCard && createPortal(<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{
      margin: 0,
      background: 'rgba(17,24,39,0.5)'
    }} data-id="element-7034">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden" data-id="element-7035">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-7036">
                <h3 className="text-base font-semibold text-gray-900 flex items-center" data-id="element-7037">
                  <ShieldCheckIcon size={18} className="mr-2 text-[#9F140F]" data-id="element-7038" />
                  {t('components.CardOnFilePayment.confirm_charge')}
                </h3>
                <button onClick={() => setShowPreviewModal(false)} aria-label="Close" className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-7039">
                  <XIcon size={18} aria-hidden="true" data-id="element-7040" />
                </button>
              </div>

              <div className="p-6 space-y-5" data-id="element-7041">
                <div className="text-center pb-2" data-id="element-7042">
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1" data-id="element-7043">
                    {t('components.BulkPaymentPreview.amount')}
                  </p>
                  <p className="text-3xl font-bold text-gray-900" data-id="element-7044">
                    {currency} {amount}
                  </p>
                </div>

                <div className="divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden" data-id="element-7045">
                  <div className="flex justify-between items-start px-4 py-3 bg-gray-50/50" data-id="element-7046">
                    <span className="text-sm text-gray-500" data-id="element-7047">
                      {t('components.BulkPaymentPreview.customer')}
                    </span>
                    <div className="text-right" data-id="element-7048">
                      <p className="text-sm font-medium text-gray-900" data-id="element-7049">
                        {selectedCustomer.name}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-7050">
                        {selectedCustomer.email}
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-between items-center px-4 py-3 bg-gray-50/50" data-id="element-7051">
                    <span className="text-sm text-gray-500" data-id="element-7052">
                      {t('components.CardOnFilePayment.card')}
                    </span>
                    <span className="text-sm font-medium text-gray-900" data-id="element-7053">
                      {selectedCard.brand} •••• {selectedCard.last4}
                    </span>
                  </div>
                  {reference && <div className="flex justify-between items-center px-4 py-3 bg-gray-50/50" data-id="element-7054">
                      <span className="text-sm text-gray-500" data-id="element-7055">
                        {t('components.BulkPaymentPreview.reference')}
                      </span>
                      <span className="text-sm font-medium text-gray-900" data-id="element-7056">
                        {reference}
                      </span>
                    </div>}
                  {description && <div className="flex justify-between items-center px-4 py-3 bg-gray-50/50" data-id="element-7057">
                      <span className="text-sm text-gray-500" data-id="element-7058">
                        {t('components.BulkPaymentPreview.description')}
                      </span>
                      <span className="text-sm font-medium text-gray-900" data-id="element-7059">
                        {description}
                      </span>
                    </div>}
                </div>

                <div className="bg-[#FEE7E5] border border-[#FBD4D1] rounded-lg p-3 flex items-start" data-id="element-7060">
                  <AlertCircleIcon size={16} className="text-[#F1786F] mr-2 mt-0.5 flex-shrink-0" data-id="element-7061" />

                  <p className="text-xs text-[#EB4A42]" data-id="element-7062">
                    {t('components.CardOnFilePayment.this_will_immediately_charge_the_customers')}
                  </p>
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end space-x-3" data-id="element-7063">
                <button onClick={() => setShowPreviewModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-7064">
                  {t('components.CardOnFilePayment.cancel')}
                </button>
                <button onClick={handleConfirmCharge} className="px-5 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm flex items-center" data-id="element-7065">
                  <CheckIcon size={16} className="mr-1.5" data-id="element-7066" />
                  {t('components.CardOnFilePayment.confirm_charge_2')}
                </button>
              </div>
            </div>
          </div>, document.body)}
    </div>;
}